import React from 'react';
import { ALLIANCE_THRESHOLDS } from './alliance-tokens';

/**
 * Tracks request failures for the connection banner. Apps report every
 * query/mutation result here (from the TanStack QueryCache/MutationCache
 * callbacks), and the banner shows after 3+ connection failures within 30s
 * while the browser says it is online, then clears after 2 successes.
 *
 * Only network errors and 5xx count. 4xx responses are the API answering,
 * so they never trip the banner.
 */

let failures: number[] = [];
let recoveries = 0;
let troubled = false;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

/** True for errors that mean we couldn't reach the server (or it broke) */
export const isConnectionFailure = (error: unknown) => {
  const err = error as {
    response?: { status?: number };
    message?: string;
    code?: string;
  } | null;
  if (!err) {
    return false;
  }
  const status = err.response?.status;
  if (status !== undefined) {
    return status >= 500;
  }
  return (
    err.code === 'ECONNABORTED' ||
    /network error|failed to fetch|timeout|load failed/i.test(err.message || '')
  );
};

/** Report a failed request */
export const reportRequestFailure = (error: unknown) => {
  if (!isConnectionFailure(error)) {
    return;
  }
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return;
  }
  const now = Date.now();
  failures = failures
    .filter((time) => now - time < ALLIANCE_THRESHOLDS.connectionWindowMs)
    .concat(now);
  recoveries = 0;
  if (!troubled && failures.length >= ALLIANCE_THRESHOLDS.connectionFailures) {
    troubled = true;
    emit();
  }
};

/** Report a successful request */
export const reportRequestSuccess = () => {
  if (!troubled) {
    return;
  }
  recoveries += 1;
  if (recoveries >= ALLIANCE_THRESHOLDS.connectionRecoveries) {
    troubled = false;
    failures = [];
    recoveries = 0;
    emit();
  }
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** True while the connection banner should show */
export function useConnectionTrouble() {
  return React.useSyncExternalStore(subscribe, () => troubled);
}
